import { useEffect, useRef, useState } from 'react';
import type { KpiData } from '../pages/protected/controller/helpers/types';
import { WS_STALE_THRESHOLD_MS } from "../pages/protected/controller/helpers/constants"
import { getDashboardSummary } from '../api/controllerDashboardApis';
import logger from '../utils/logger';
import { recordingSocket } from "../components/mediasoup/recordingSocket"
interface UseControllerStatsResult {
  data: KpiData | null;
  isStale: boolean;
  isLoading: boolean;
  error: string | null;
}

function flattenStats(raw: any): KpiData {
  return {
    liveStreams:      raw.streamCount?.liveStreamsCount                     ?? 0,
    totalCourtrooms:  raw.streamCount?.courtRoomActiveCount                 ?? 0,
    archivalPending:  raw.archivalRequestCount?.archivalPendingCount        ?? 0,
    archivalTotal:    raw.archivalRequestCount?.archivalRequestCount        ?? 0,
    flaggedCount:     raw.flaggedCount?.flaggedCount                        ?? 0,
    flagPIICount:     raw.flaggedCount?.flaggedTypeCount?.flagPIICount      ?? 0,
    flagConductCount: raw.flaggedCount?.flaggedTypeCount?.flagConductCount  ?? 0,
    flagOtherCount:   raw.flaggedCount?.flaggedTypeCount?.flaggedOtherCount ?? 0,
    recordingToday:   raw.recordingTodayCount?.recordingTodayCount          ?? 0,
    storageTodayGB:   raw.recordingTodayCount?.storageTodayGB              ?? "0.00",
  };
}

export function useControllerStats(): UseControllerStatsResult {
  const [data, setData]         = useState<KpiData | null>(null);
  const [isStale, setIsStale]   = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [error, setError]       = useState<string | null>(null);

  const staleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetStaleTimer = () => {
    if (staleTimer.current) clearTimeout(staleTimer.current);
    setIsStale(false);
    staleTimer.current = setTimeout(() => setIsStale(true), WS_STALE_THRESHOLD_MS);
  };

  // Initial fetch
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await getDashboardSummary();
        setData(flattenStats(res.data));
        resetStaleTimer();
      } catch (err: any) {
        logger.error("📊 Summary error:", err);
        setError(err?.message ?? 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);


  useEffect(() => {
    const handleStatsUpdate = (raw: any) => {
      setData(flattenStats(raw));
      resetStaleTimer();
    };

    recordingSocket.on("connect", resetStaleTimer);
    recordingSocket.on("controller_stats_update", handleStatsUpdate);
    recordingSocket.on("disconnect", () => setIsStale(true));

    return () => {
      recordingSocket.off("connect", resetStaleTimer);
      recordingSocket.off("controller_stats_update", handleStatsUpdate);
      recordingSocket.off("disconnect");
      if (staleTimer.current) clearTimeout(staleTimer.current);
    };
  }, []);

  return { data, isStale, isLoading, error };
}